import { useEffect, useMemo } from 'react'
import { Button } from 'antd'
import useProjectStore from '../project.store'
import useGetDataProject from './use-get-data-project'
import { setMenuRefetchSignal } from '../stores/project-reducer'
import {
  generateDynamicListMenuListProject,
  generateGiaoViecListMenu,
  LIST_MENU_LIST_PROJECT_CONFIG,
} from '../../../constants/list-menu.quan-ly-giao-viec'
import {
  CustomMenuItemType,
  NO_NAVIGATE_KEY,
} from '../../../../e-office-layout/components/dynamic-menu/custom-menu-items-type'
import IconGlobalApp from '../../../../../utils/IconGlobalApp'
import { mapCommonIcons } from '../../../../../utils/mapCommonIcons'
import {
  PROJECT_DEPARTMENT_PREFIX_URL,
  PROJECT_GENERAL_PREFIX_URL,
} from '../constants/config.project'

type Params = {
  projectId: string | null | undefined
  pathname: string
  isAdminGiaoViec: boolean
  isInQLGiaoViec: boolean
}

const useControlLeftMenuProjectItems = ({
  projectId,
  pathname,
  isAdminGiaoViec,
  isInQLGiaoViec,
}: Params) => {
  const { menuRefetchSignal } = useProjectStore()

  const {
    loading: loadingDepProject,
    getData: getDepProjects,
    data: depProjects,
    metadata: metadataDepProject,
    getDetail: getDepProject,
  } = useGetDataProject({
    filter: { departmentIds: [], isTemplate: false },
    listVariant: isAdminGiaoViec ? 'all' : 'for-me',
  })

  const depNextPageId = metadataDepProject?.paging?.cursor?.next

  const {
    loading: loadingGenProject,
    getData: getGenProjects,
    data: genProjects,
    metadata: metadataGenProject,
    getDetail: getGenProject,
  } = useGetDataProject({
    filter: { departmentIds: null, isTemplate: false },
    listVariant: isAdminGiaoViec ? 'all' : 'for-me',
  })

  const genNextPageId = metadataGenProject?.paging?.cursor?.next

  const LIST_MENU_GIAO_VIEC = useMemo(() => {
    const LIST_MENU_LIST_PROJECT = generateDynamicListMenuListProject({
      generateDeps: (baseUrl) => {
        const result: CustomMenuItemType<'item'>[] = depProjects.map(
          ({ id, title }) => ({
            key: `${baseUrl}/${id}`,
            label: title,
            title,
          })
        )

        if (depNextPageId)
          result.push({
            key: `${NO_NAVIGATE_KEY}${baseUrl}-loadMore`,
            label: (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() =>
                  getDepProjects({
                    type: 'load-more',
                    paging: { pageLimit: 5, pageId: depNextPageId },
                  })
                }
                icon={
                  <IconGlobalApp size={14} name={mapCommonIcons.loadMore} />
                }
              >
                Tải thêm
              </Button>
            ),
            title: 'Tải thêm',
          })

        return result
      },
      generateGenerals: (baseUrl) => {
        const result: CustomMenuItemType<'item'>[] = genProjects.map(
          ({ id, title }) => ({
            key: `${baseUrl}/${id}`,
            label: title,
            title,
          })
        )

        if (genNextPageId)
          result.push({
            key: `${NO_NAVIGATE_KEY}${baseUrl}-loadMore`,
            label: (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() =>
                  getGenProjects({
                    type: 'load-more',
                    paging: { pageLimit: 5, pageId: genNextPageId },
                  })
                }
                icon={
                  <IconGlobalApp size={14} name={mapCommonIcons.loadMore} />
                }
              >
                Tải thêm
              </Button>
            ),
            title: 'Tải thêm',
          })

        return result
      },
      loadingDeps: loadingDepProject,
      loadingGenerals: loadingGenProject,
    })

    if (isAdminGiaoViec)
      return generateGiaoViecListMenu([
        ...LIST_MENU_LIST_PROJECT,
        LIST_MENU_LIST_PROJECT_CONFIG,
      ])
    return generateGiaoViecListMenu(LIST_MENU_LIST_PROJECT)
  }, [
    depProjects,
    genProjects,
    isAdminGiaoViec,
    loadingDepProject,
    loadingGenProject,
    depNextPageId,
    genNextPageId,
  ])

  useEffect(() => {
    if (!projectId || !pathname) return
    if (pathname.includes(PROJECT_DEPARTMENT_PREFIX_URL))
      getDepProject({
        id: projectId,
        refetchInList: true,
      })
    if (pathname.includes(PROJECT_GENERAL_PREFIX_URL))
      getGenProject({
        id: projectId,
        refetchInList: true,
      })
  }, [projectId])

  // Refetch khi có signal (sau khi create/update/delete 1 project)
  useEffect(() => {
    const onFinish = () => setMenuRefetchSignal(undefined)

    if (!menuRefetchSignal) return
    const { action, type } = menuRefetchSignal

    if (action === 'U' && projectId) {
      if (type === 'DEPARTMENT_PROJECT')
        getDepProject({
          id: projectId,
          refetchInList: true,
          localHandler: { onGetDetailFinish: onFinish },
        })
      if (type === 'GENERAL_PROJECT')
        getGenProject({
          id: projectId,
          refetchInList: true,
          localHandler: { onGetDetailFinish: onFinish },
        })
      return
    }

    if (type === 'DEPARTMENT_PROJECT')
      getDepProjects({
        type: 're-fetch',
        paging: {
          pageLimit:
            action === 'U'
              ? Math.max(5, Math.min(depProjects.length, 1000))
              : 5,
        },
        localHandler: { onGetListFinish: onFinish },
      })

    if (type === 'GENERAL_PROJECT')
      getGenProjects({
        type: 're-fetch',
        paging: {
          pageLimit:
            action === 'U'
              ? Math.max(5, Math.min(genProjects.length, 1000))
              : 5,
        },
        localHandler: { onGetListFinish: onFinish },
      })
  }, [menuRefetchSignal])

  useEffect(() => {
    if (isInQLGiaoViec) {
      getDepProjects({ type: 're-fetch', paging: { pageLimit: 5 } })
      getGenProjects({ type: 're-fetch', paging: { pageLimit: 5 } })
    }
  }, [isInQLGiaoViec, isAdminGiaoViec])

  return {
    LIST_MENU_GIAO_VIEC,
  }
}

export default useControlLeftMenuProjectItems
